/**
 * Envelope encryption utilities for per-report DEK generation,
 * user-path wrapping (AES-GCM), and admin OPK wrapping.
 */

// --- Base64 helpers ---

export function bufferToBase64(bytes: Uint8Array): string {
    let binary = "";
    for (let i = 0; i < bytes.byteLength; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
}

export function base64ToBuffer(b64: string): Uint8Array {
    const binary = atob(b64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
}

// --- Per-report DEK generation ---

export function generatePerReportDek(): Uint8Array {
    return crypto.getRandomValues(new Uint8Array(32));
}

// --- User-path DEK wrapping (AES-GCM) ---

export async function wrapDekForUser(
    userDek: ArrayBuffer,
    perReportDek: Uint8Array,
): Promise<{ user_wrapped_dek: string; user_dek_iv: string }> {
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const key = await crypto.subtle.importKey("raw", userDek, { name: "AES-GCM" }, false, ["encrypt"]);
    const encrypted = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, perReportDek);
    return {
        user_wrapped_dek: bufferToBase64(new Uint8Array(encrypted)),
        user_dek_iv: bufferToBase64(iv),
    };
}

export async function unwrapDekFromUser(
    userDek: ArrayBuffer,
    wrappedDek: string,
    iv: string,
): Promise<Uint8Array> {
    const key = await crypto.subtle.importKey("raw", userDek, { name: "AES-GCM" }, false, ["decrypt"]);
    const decrypted = await crypto.subtle.decrypt(
        { name: "AES-GCM", iv: base64ToBuffer(iv) },
        key,
        base64ToBuffer(wrappedDek),
    );
    return new Uint8Array(decrypted);
}

// --- Admin OPK wrapping (AES-GCM) ---

export async function wrapOpkForAdmin(
    adminWrappingKey: ArrayBuffer,
    opkBytes: ArrayBuffer,
): Promise<{ encrypted_opk: string; wrap_iv: string }> {
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const key = await crypto.subtle.importKey("raw", adminWrappingKey, { name: "AES-GCM" }, false, ["encrypt"]);
    const encrypted = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, opkBytes);
    return {
        encrypted_opk: bufferToBase64(new Uint8Array(encrypted)),
        wrap_iv: bufferToBase64(iv),
    };
}

export async function unwrapOpkForAdmin(
    adminWrappingKey: ArrayBuffer,
    encryptedOpk: string,
    wrapIv: string,
): Promise<ArrayBuffer> {
    const key = await crypto.subtle.importKey("raw", adminWrappingKey, { name: "AES-GCM" }, false, ["decrypt"]);
    return crypto.subtle.decrypt(
        { name: "AES-GCM", iv: base64ToBuffer(wrapIv) },
        key,
        base64ToBuffer(encryptedOpk),
    );
}
